import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import ReactFlow, { Background, Controls, MiniMap, useNodesState, useEdgesState, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

const typeColors = { source: "#6366F1", raw: "#2563EB", staging: "#0EA5E9", mart: "#059669", dimension: "#8B5CF6", dashboard: "#F59E0B", ml_feature: "#EF4444", alert: "#F97316", unknown: "#6B7280" };
const statusBorder = { critical: "#EF4444", warning: "#F59E0B", healthy: "#10B981" };

function LineageNode({ data }) {
  const border = statusBorder[data.status] || "#1B2A47";
  const tc = typeColors[data.type] || typeColors.unknown;
  return (
    <div className="rounded-lg px-3 py-2.5 min-w-[155px] transition-all hover:shadow-lg" style={{ background: "#FFFFFF", border: `1px solid rgba(30,58,95,0.08)`, borderLeft: `4px solid ${border}` }} data-testid={`lineage-node-${data.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: tc, boxShadow: `0 0 6px ${tc}40` }} />
        <span className="font-mono text-[11px] font-bold truncate text-[#C0CDD8]">{data.label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[8px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 rounded" style={{ background: `${tc}15`, color: tc }}>{data.type}</span>
        {data.status !== "healthy" && (
          <span className="text-[8px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 rounded animate-pulse" style={{ background: `${border}15`, color: border }}>{data.status}</span>
        )}
      </div>
    </div>
  );
}

const nodeTypes = { lineageNode: LineageNode };

function computeLayout(rawNodes) {
  const order = { source: 0, raw: 1, staging: 2, dimension: 2, mart: 3, dashboard: 4, ml_feature: 4, alert: 4, unknown: 2 };
  const columns = {};
  rawNodes.forEach((n) => {
    const col = order[n.type] ?? 2;
    if (!columns[col]) columns[col] = [];
    columns[col].push(n);
  });
  const positioned = [];
  Object.entries(columns).forEach(([col, nodes]) => {
    const x = parseInt(col) * 300;
    nodes.forEach((n, i) => {
      positioned.push({ id: n.id, type: "lineageNode", position: { x, y: i * 110 + 40 }, data: { ...n } });
    });
  });
  return positioned;
}

export default function LineageExplorerPage() {
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    api.get("/lineage").then((r) => {
      setLineage(r.data);
      setNodes(computeLayout(r.data.nodes || []));
      setEdges((r.data.edges || []).map((e, i) => ({
        id: `edge-${i}`, source: e.source, target: e.target,
        animated: true,
        style: { stroke: "#1E3A5F", strokeWidth: 1.5, opacity: 0.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#1E3A5F", width: 14, height: 14 },
      })));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [setNodes, setEdges]);

  const legend = [
    { type: "source", label: "Source" }, { type: "raw", label: "Raw" },
    { type: "staging", label: "Staging" }, { type: "dimension", label: "Dimension" },
    { type: "mart", label: "Mart" }, { type: "dashboard", label: "Dashboard" },
    { type: "ml_feature", label: "ML Feature" }, { type: "alert", label: "Alert" },
  ];

  if (loading) return (
    <div className="p-6 space-y-4" data-testid="lineage-loading">
      <Skeleton className="h-8 w-48" /><Skeleton className="h-[600px]" />
    </div>
  );

  return (
    <div className="p-6 space-y-4" data-testid="lineage-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Lineage Explorer</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Data asset dependency graph - drag nodes to rearrange</p>
        </div>
        {lineage && <Badge variant="outline" className="font-mono text-[10px]">{lineage.nodes?.length} nodes / {lineage.edges?.length} edges</Badge>}
      </div>

      <div className="flex flex-wrap items-center gap-4 sf-card px-4 py-2.5" data-testid="lineage-legend">
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "rgba(30,58,95,0.6)" }}>Type:</span>
        {legend.map((l) => (
          <div key={l.type} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full shadow-sm" style={{ background: typeColors[l.type], boxShadow: `0 0 4px ${typeColors[l.type]}40` }} />
            <span className="text-[10px] font-medium">{l.label}</span>
          </div>
        ))}
        <div className="w-px h-4 bg-border mx-1" />
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "rgba(30,58,95,0.6)" }}>Status:</span>
        {[{ s: "healthy", c: "#10B981" }, { s: "warning", c: "#F59E0B" }, { s: "critical", c: "#EF4444" }].map(({ s, c }) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className="w-8 h-1.5 rounded-full" style={{ background: c }} />
            <span className="text-[10px] font-medium capitalize">{s}</span>
          </div>
        ))}
      </div>

      <div className="sf-card overflow-hidden" style={{ height: "620px" }} data-testid="lineage-graph">
        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView fitViewOptions={{ padding: 0.15 }}
          minZoom={0.2} maxZoom={2.5}
          nodesDraggable={true}
          defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
        >
          <Background gap={24} size={1} color="hsl(var(--border))" variant="dots" />
          <Controls showInteractive={false} className="bg-card border border-border rounded-sm" />
          <MiniMap nodeColor={(n) => typeColors[n.data?.type] || "#6B7280"} className="bg-card border border-border rounded-sm" maskColor="hsl(var(--background) / 0.8)" />
        </ReactFlow>
      </div>
    </div>
  );
}
